'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@algoacademy/design-system/shared/cn';
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { toast } from '@algoacademy/design-system/web/feedback/Toaster/Toaster';
import { showErrorToast } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
type IssuedOfferLinkProps = {
  offerUrl: string;
  variant: 'sent' | 'emailFailed' | 'unpaid' | 'paid';
};
const COPY_BY_VARIANT: Record<IssuedOfferLinkProps['variant'], {
  title: string;
  description: string;
}> = {
  sent: {
    title: 'Link do oferty wysłany',
    description: 'E-mail z tym linkiem został wysłany kupującemu. Link prowadzi go do płatności w Stripe.'
  },
  emailFailed: {
    title: 'Link do oferty',
    description: 'E-mail nie został wysłany. Skopiuj link i przekaż go kupującemu ręcznie - prowadzi do płatności w Stripe.'
  },
  unpaid: {
    title: 'Istniejący link do oferty',
    description: 'Ten link został wystawiony wcześniej i nadal prowadzi do płatności w Stripe.'
  },
  paid: {
    title: 'Link ucznia',
    description: 'Oferta jest już opłacona. Możesz zapisać zmiany w celu zmienienia ustawień oferty.'
  }
};

/**
 * The only place an issued link is ever shown - nothing lists offers - so the
 * address is printed in full, stays put after the toast fades, and is plain
 * selectable text rather than a link: opening it would start a Stripe payment
 * or, once the offer is paid, open the buyer's own Stripe panel.
 *
 * The green, "success" look is earned only by `sent`: the e-mail carrying
 * the link actually went out. `emailFailed` is also a freshly minted link,
 * but the send failed, so green would misreport that as a success - it gets
 * the neutral panel style instead, the one this form already uses elsewhere
 * (see StudentSubscriptionPanel). `unpaid` and `paid` share that same
 * neutral style for a different reason: both describe a link that already
 * existed before this render, so showing it again is not news. The `paid`
 * variant in particular must never read like a success confirmation - the
 * offer being paid is not something that just happened here.
 */
export function IssuedOfferLink(t0) {
  const $ = _c(22);
  const {
    offerUrl,
    variant
  } = t0;
  const copy = COPY_BY_VARIANT[variant];
  const isFreshlySent = variant === "sent";
  const t1 = isFreshlySent ? "border-borderSuccess" : "border-borderPrimary";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex w-full flex-col gap-3 rounded-lg border bg-backgroundSecondary p-4", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isFreshlySent ? "text-contentGreen" : "text-contentPrimary";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("font-medium text-sm", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== copy.title || $[5] !== t4) {
    t5 = <p className={t4}>{copy.title}</p>;
    $[4] = copy.title;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== offerUrl) {
    t6 = <p className="w-full break-all font-mono text-contentPrimary text-sm">{offerUrl}</p>;
    $[7] = offerUrl;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== copy.description) {
    t7 = <p className="text-contentSecondary text-sm">{copy.description}</p>;
    $[9] = copy.description;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== offerUrl) {
    t8 = async () => {
      try {
        await navigator.clipboard.writeText(offerUrl);
        toast.success("Skopiowano link do oferty");
      } catch {
        showErrorToast("Nie uda\u0142o si\u0119 skopiowa\u0107 linku. Zaznacz go i skopiuj r\u0119cznie.");
      }
    };
    $[11] = offerUrl;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ClipboardDocumentIcon data-slot="icon" />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8) {
    t10 = <Button outline={true} type="button" className="w-full" onClick={t8}>{t9}Kopiuj link</Button>;
    $[14] = t8;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t2 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t11 = <div role="status" className={t2}>{t5}{t6}{t7}{t10}</div>;
    $[16] = t10;
    $[17] = t2;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}